<template>
  <div>

    <!-- Mobile overlay -->
    <div 
      v-if="isMobile && !collapsed" 
      class="sidebar-overlay" 
      @click="closeSidebar"
    ></div>

    <!-- Sidebar -->
    <aside class="sidebar" :class="{ open: !collapsed }">

      <!-- SCROLL ONLY ON MOBILE -->
      <div :class="['sidebar-inner', { 'mobile-scroll': isMobile }]">

        <nav class="sidebar-menu">
          <div 
            v-for="item in filteredMenu" 
            :key="item.title" 
            class="menu-item"
            @click="handleClick(item)"
          >
            <div class="icon-box">
              <i :class="item.icon"></i>
            </div>

            <span class="menu-label">{{ item.title }}</span>
          </div>
        </nav>

      </div>

    </aside>

  </div>
</template>


<script>
import authStore from "../store/auth-store";

export default {
  props: {
    collapsed: { type: Boolean, required: true }
  },

  emits: ["onItemClicked", "request-system-change"],

  data() {
    return {
      menu: [],
      currentUser: null,
      windowWidth: window.innerWidth
    };
  },

  computed: {
    user() {
      return authStore.state.user;
    },

    isMobile() {
      return this.windowWidth <= 1024;
    },

    filteredMenu() {
      return this.menu.filter(item => !item.hidden);
    }
  },

  mounted() {
    this.currentUser = this.user;
    this.buildMenu();
    window.addEventListener("resize", this.handleResize);
  },

  beforeUnmount() {
    window.removeEventListener("resize", this.handleResize);
  },

  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },

    closeSidebar() {
      this.$emit("onItemClicked", true);
    },

    handleClick(item) {
      if (item.id === "change-system") {
        this.$emit("request-system-change");
        this.$emit("onItemClicked", true);
        return;
      }

      if (!item.external) this.$router.push(item.href);
      else window.open(item.href, "_blank");

      if (this.isMobile) this.$emit("onItemClicked", true);
    },

    isLoggedIn() {
      return this.currentUser != null;
    },

    isAdmin() {
      return this.isLoggedIn() && this.currentUser.role === "Admin";
    },

    buildMenu() {
      this.menu = [
        { title: "Home", icon: "fas fa-home", href: "/" },
        { title: "About", icon: "fas fa-info-circle", href: "/about" },
        {
          title: "E-Learning",
          icon: "fa-solid fa-chalkboard-user",
          href: "https://learning.e-cat.uk/",
          external: true
        },
        { title: "Contact", icon: "far fa-address-book", href: "/contact" },

        {
          title: "Login",
          icon: "far fa-user",
          href: "/login",
          hidden: this.isLoggedIn()
        },

        {
          title: "My Assessments",
          icon: "far fa-file-alt",
          href: "/admin/report",
          hidden: !(this.isLoggedIn() && this.isAdmin())
        },
        {
          title: "Referrals Received",
          icon: "far fa-file-alt",
          href: "/admin/referrals",
          hidden: !this.isLoggedIn()
        },
        {
          title: "Reports",
          icon: "far fa-file-alt",
          href: "/admin/user-report",
          hidden: !(this.isLoggedIn() && !this.isAdmin())
        },

        {
          title: "Screening Questions",
          icon: "fas fa-question-circle",
          href: "/admin/screening-questions",
          hidden: !(this.isLoggedIn() && this.isAdmin())
        },
        {
          title: "Survey Questions",
          icon: "far fa-question-circle",
          href: "/admin/survey-questions",
          hidden: !(this.isLoggedIn() && this.isAdmin())
        },
        {
          title: "About Us Management",
          icon: "fa-solid fa-circle-info",
          href: "/admin/about-us-management",
          hidden: !(this.isLoggedIn() && this.isAdmin())
        },
        {
          title: "Contact Us Management",
          icon: "fa-solid fa-address-book",
          href: "/admin/contact-us-management",
          hidden: !(this.isLoggedIn() && this.isAdmin())
        },

        {
          title: "Account Details",
          icon: "fas fa-key",
          href: "/account",
          hidden: !this.isLoggedIn()
        },
        {
          title: "Logout",
          icon: "fas fa-sign-out-alt",
          href: "/logout",
          hidden: !this.isLoggedIn()
        },

        {
          title: "Change System",
          icon: "fas fa-sync-alt",
          id: "change-system"
        }
      ];
    }
  }
};
</script>

<style scoped lang="scss">
$sidebar-bg: #2a2a2e;
$icon-bg: #1e1e22;
$highlight-blue: #4d8bff;
$text-color: #ffffff;

.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  width: 60px;
  height: 100vh;
  background: $sidebar-bg;
  border-right: 1px solid #1f1f22;
  padding-top: 8px;
  z-index: 9999;
  transition: transform 0.3s ease;
  transform: translateX(-100%);
}

@media (max-width: 1024px) {
  .sidebar {
    top: 45px; 
    height: calc(100vh - 45px);
  }
}

.sidebar.open {
  transform: translateX(0);
}

/*** INNER — SCROLL ONLY ON MOBILE */
.sidebar-inner {
  height: 100%;
  overflow: visible; // desktop
}

.sidebar-inner.mobile-scroll {
  overflow-y: auto;  // mobile only
  overflow-x: hidden;
}

/* SCROLLBAR MOBILE ONLY */
.sidebar-inner.mobile-scroll::-webkit-scrollbar {
  width: 6px;
}

.sidebar-inner.mobile-scroll::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 4px;
}

/*** MENU */
.sidebar-menu {
  display: flex;
  flex-direction: column;
  gap: 6px;   // compact
}

/*** MENU ITEM */
.menu-item {
  position: relative;
  height: 44px;    // compact
  margin-left: 6px;
  width: 50px;     // compact
  display: flex;
  align-items: center;
  cursor: pointer;
  border-radius: 8px;
  overflow: visible;
  transition: all 0.25s ease;
}

.icon-box {
  width: 44px;
  height: 44px;
  background: $icon-bg;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;

  i {
    color: white;
    font-size: 15px;
  }
}

.menu-label {
  position: absolute;
  left: 60px; 
  font-size: 14px;
  color: $text-color;
  opacity: 0;
  white-space: nowrap;
  transition: opacity 0.25s ease;
}

/*** DESKTOP HOVER ONLY */
@media (min-width: 1025px) {
  .menu-item:hover {
    width: 240px;
    background: $highlight-blue;
  }

  .menu-item:hover .menu-label {
    opacity: 1;
  }

  .menu-item:hover .icon-box {
    background: transparent;
  }
}

@media (min-width: 1025px) {
  .sidebar {
    transform: translateX(0) !important;
  }
}

/*** MOBILE OVERLAY */
.sidebar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 1998;
}
</style>
